/**
 * The final two steps of a cloud runtime connection attempt — POST `/connect`
 * with retry while the VM boots, then register the returned descriptor as a
 * connected saved environment — plus the orchestrator that runs the whole
 * ensure -> start -> connect -> register sequence.
 *
 * Extracted from the original `connectCloudRuntimeProvisioning.ts`, which grew
 * past its own cap once `/ensure` and `/start` were added: those two steps
 * moved to `connectCloudRuntimeEnsureAndStart.ts`, and the primitives every
 * step shares (abort/retry/progress) moved to `connectCloudRuntimeShared.ts`.
 * `runCloudRuntimeConnection` is the seam `connectCloudRuntime.ts`'s flight
 * layer calls — it does not know or care what is inside it.
 */
import { addConnectedSavedEnvironment } from "../../environments/runtime";
import { resolveForgeCloudEnvironmentLabel } from "../../environments/forgeCloudEnvironmentLabel";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";
import { buildPrimaryFetchInit } from "~/environments/primary/fetchInit";
import type { SidebarPreConnectCloudMetadata } from "./SidebarProjectItem";
import {
  clearCloudRuntimeProjectHandoff,
  recordCloudRuntimeProjectHandoff,
} from "./cloudRuntimeProjectHandoffStore";
import { cloudRuntimeRetryDelayMs } from "../../environments/runtime/cloudRuntimeConnectRetry";
import { connectAttemptBudget } from "./connectAttemptBudget";
import {
  CONNECT_MAX_ATTEMPTS,
  emitConnectProgress,
  formatRetryDelay,
  retryAfterHeader,
  shouldRetryConnectResponse,
  sleepWithAbort,
  throwCloudRuntimeConnectFailure,
  throwIfAborted,
  type CloudRuntimeConnectionDescriptor,
  type CloudRuntimeConnectionOptions,
} from "./connectCloudRuntimeShared";
import { ensureCloudRuntime, startStoppedCloudRuntime } from "./connectCloudRuntimeEnsureAndStart";
import type { CloudRuntimeConnectProgressReporter } from "./connectCloudRuntime";
import { buildCmmdRuntimeEnvironmentId } from "../../environments/cmmdRuntimeEnvironmentId";

async function readCloudRuntimeDescriptor(
  response: Response,
  body: unknown,
  onProgress: CloudRuntimeConnectProgressReporter | undefined,
): Promise<CloudRuntimeConnectionDescriptor> {
  if (!response.ok) {
    throwCloudRuntimeConnectFailure(response, body, { stage: "connect-request" });
  }

  const descriptor = body as {
    httpBaseUrl?: string;
    wsBaseUrl?: string;
    bearerToken?: string;
    expiresAt?: string;
    runtime?: { runtimeId?: string; projectId?: string | number };
  };
  if (!descriptor.httpBaseUrl || !descriptor.bearerToken) {
    throw new Error("Cloud VM connect returned an invalid connection descriptor.");
  }
  emitConnectProgress(onProgress, {
    stage: "connection-descriptor",
    summary: "Received runtime connection descriptor",
    detail: descriptor.httpBaseUrl,
  });
  return {
    httpBaseUrl: descriptor.httpBaseUrl,
    bearerToken: descriptor.bearerToken,
    ...(descriptor.wsBaseUrl ? { wsBaseUrl: descriptor.wsBaseUrl } : {}),
    ...(descriptor.expiresAt ? { expiresAt: descriptor.expiresAt } : {}),
  };
}

async function requestCloudRuntimeDescriptor(
  runtimeId: string,
  meta: SidebarPreConnectCloudMetadata,
  options: CloudRuntimeConnectionOptions,
  maxAttempts: number = CONNECT_MAX_ATTEMPTS,
): Promise<CloudRuntimeConnectionDescriptor> {
  const { signal, onProgress } = options;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    throwIfAborted(signal);
    emitConnectProgress(onProgress, {
      stage: "connect-request",
      summary: attempt === 1 ? "Requesting cloud runtime connection" : "Retrying cloud runtime",
      detail: `/api/cmmd/forge-runtimes/${runtimeId}/connect`,
      attempt,
      maxAttempts,
    });
    const response = await fetch(
      resolvePrimaryEnvironmentHttpUrl(
        `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/connect`,
      ),
      buildPrimaryFetchInit({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organizationId: meta.cmmdOrganizationId }),
        signal,
      }),
    );
    const body = await response.json().catch(() => ({}));
    if (!shouldRetryConnectResponse(response, body, attempt, maxAttempts)) {
      return readCloudRuntimeDescriptor(response, body, onProgress);
    }
    const delayMs = cloudRuntimeRetryDelayMs({
      attempt,
      retryAfter: retryAfterHeader(response),
    });
    emitConnectProgress(onProgress, {
      stage: "connect-retry",
      summary: "Cloud runtime is still booting",
      detail: `CMMD returned ${response.status}; retrying in ${formatRetryDelay(delayMs)}.`,
      attempt,
      maxAttempts,
    });
    await sleepWithAbort(delayMs, signal);
  }
  throw new Error("Cloud runtime connection retry budget was exhausted.");
}

/**
 * Saves the Environment a cloud connect landed on and connects to it. The
 * record is labelled by its Organization, never by the Project that connected.
 */
async function registerCloudRuntimeEnvironment(
  runtimeId: string,
  environmentId: string,
  meta: SidebarPreConnectCloudMetadata,
  descriptor: CloudRuntimeConnectionDescriptor,
  options: CloudRuntimeConnectionOptions,
): Promise<void> {
  const { signal, onProgress } = options;
  emitConnectProgress(onProgress, {
    stage: "environment-register",
    summary: "Registering cloud project",
    detail: meta.projectName,
  });
  await addConnectedSavedEnvironment({
    environment: {
      httpBaseUrl: descriptor.httpBaseUrl,
      wsBaseUrl: descriptor.wsBaseUrl ?? "",
      // CMMD owns this identifier. The runtime id still selects the current
      // Sprite route, but it must not become the saved Environment key because
      // a replacement receives a new runtime id.
      environmentId,
      // The Environment belongs to the person, not to the project being
      // connected. Writing the project's name here renamed the machine every
      // sibling project was already on, and writing its id made the record
      // look like a per-project runtime.
      label: resolveForgeCloudEnvironmentLabel(meta.cmmdOrganizationId),
      kind: "project",
      runtimeId,
      cmmdOrganizationId: String(meta.cmmdOrganizationId),
      ...(environmentId !== buildCmmdRuntimeEnvironmentId(runtimeId) && meta.runtimeId
        ? { replacesEnvironmentId: buildCmmdRuntimeEnvironmentId(meta.runtimeId) }
        : {}),
      ...(descriptor.expiresAt ? { runtimeTicketExpiresAt: descriptor.expiresAt } : {}),
    },
    bearerToken: descriptor.bearerToken,
    signal,
    onBootstrapRetry: ({ attempt, maxAttempts, retryDelayMs, error }) => {
      emitConnectProgress(onProgress, {
        stage: "environment-register-retry",
        summary: "Cloud project is still starting",
        detail: `${error instanceof Error ? error.message : String(error)} Retrying in ${Math.ceil(retryDelayMs / 1_000)}s.`,
        attempt,
        maxAttempts,
      });
    },
  });
  throwIfAborted(signal);
}

export async function runCloudRuntimeConnection(
  meta: SidebarPreConnectCloudMetadata,
  options: CloudRuntimeConnectionOptions,
): Promise<string> {
  const ensured = await ensureCloudRuntime(meta, options);
  const runtimeId = ensured.runtimeId;
  await startStoppedCloudRuntime(ensured, meta, options);
  const recordsRuntimeHandoff =
    runtimeId !== meta.runtimeId && typeof meta.registeredAt === "string";
  if (recordsRuntimeHandoff) {
    recordCloudRuntimeProjectHandoff({
      runtimeId,
      previousCatalogRuntimeId: meta.runtimeId,
      catalogRegisteredAt: meta.registeredAt,
      cmmdOrganizationId: meta.cmmdOrganizationId,
      cmmdProjectId: meta.cmmdProjectId,
    });
  }
  try {
    const descriptor = await requestCloudRuntimeDescriptor(
      runtimeId,
      meta,
      options,
      connectAttemptBudget(ensured),
    );
    await registerCloudRuntimeEnvironment(
      runtimeId,
      ensured.environmentId ?? buildCmmdRuntimeEnvironmentId(runtimeId),
      meta,
      descriptor,
      options,
    );
    emitConnectProgress(options.onProgress, {
      stage: "connected",
      summary: "Cloud project connected",
      detail: meta.projectName,
    });
    return runtimeId;
  } catch (error) {
    if (recordsRuntimeHandoff) clearCloudRuntimeProjectHandoff(runtimeId);
    throw error;
  }
}
